/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {AfterContentInit, ChangeDetectorRef, ContentChildren, Directive, ElementRef, Input, OnChanges, OnDestroy, QueryList, Renderer2, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {NavigationEnd} from '../events';
import {Router} from '../router';
import {RouterLink, RouterLinkWithHref} from './router_link';
/**
 * \@whatItDoes Lets you add a CSS class to an element when the link's route becomes active.
 * 
 * \@howToUse 
 * 
 * ```
 * <a routerLink="/user/bob" routerLinkActive="active-link">Bob</a>
 * ```
 * 
 * \@description 
 * 
 * The RouterLinkActive directive lets you add a CSS class to an element when the link's route
 * becomes active.
 * 
 * Consider the following example:
 * 
 * ```
 * <a routerLink="/user/bob" routerLinkActive="active-link">Bob</a>
 * ```
 * 
 * When the url is either '/user' or '/user/bob', the active-link class will
 * be added to the `a` tag. If the url changes, the class will be removed.
 * 
 * You can set more than one class, as follows:
 * 
 * ```
 * <a routerLink="/user/bob" routerLinkActive="class1 class2">Bob</a>
 * <a routerLink="/user/bob" [routerLinkActive]="['class1', 'class2']">Bob</a>
 * ```
 * 
 * You can configure RouterLinkActive by passing `exact: true`. This will add the classes
 * only when the url matches the link exactly.
 * 
 * ```
 * <a routerLink="/user/bob" routerLinkActive="active-link" [routerLinkActiveOptions]="{exact:
 * true}">Bob</a>
 * ```
 * 
 * You can assign the RouterLinkActive instance to a template variable and directly check
 * the `isActive` status.
 * ```
 * <a routerLink="/user/bob" routerLinkActive #rla="routerLinkActive">
 *   Bob {{ rla.isActive ? '(already open)' : ''}}
 * </a>
 * ```
 * 
 * Finally, you can apply the RouterLinkActive directive to an ancestor of a RouterLink.
 * 
 * ```
 * <div routerLinkActive="active-link" [routerLinkActiveOptions]="{exact: true}">
 *   <a routerLink="/user/jim">Jim</a>
 *   <a routerLink="/user/bob">Bob</a>
 * </div>
 * ```
 * 
 * This will set the active-link class on the div tag if the url is either '/user/jim' or
 * '/user/bob'.
 * 
 * \@ngModule RouterModule
 * 
 * \@stable
 */
export class RouterLinkActive implements OnChanges,
    OnDestroy, AfterContentInit {
   links: QueryList<RouterLink>;
  
  linksWithHrefs: QueryList<RouterLinkWithHref>;
private classes: string[] = [];
private subscription: Subscription;
private active: boolean = false;

   routerLinkActiveOptions: {exact: boolean} = {exact: false};
/**
 * @param {?} router
 * @param {?} element
 * @param {?} renderer
 * @param {?} cdr
 */
constructor(
private router: Router,
private element: ElementRef,
private renderer: Renderer2,
private cdr: ChangeDetectorRef) {
    this.subscription = router.events.subscribe(s => {
      if (s instanceof NavigationEnd) {
        this.update();
      }
    });
  }
/**
 * @return {?}
 */
get isActive(): boolean { return this.active; }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this.links.changes.subscribe(_ => this.update());
    this.linksWithHrefs.changes.subscribe(_ => this.update());
    this.update();
  }
/**
 * @param {?} data
 * @return {?}
 */
set routerLinkActive(data: string[]|string) {
    const /** @type {?} */ classes = Array.isArray(data) ? data : data.split(' ');
    this.classes = classes.filter(c => !!c);
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void { this.update(); }
/**
 * @return {?}
 */
ngOnDestroy(): void { this.subscription.unsubscribe(); }
/**
 * @return {?}
 */
private update(): void {
    if (!this.links || !this.linksWithHrefs || !this.router.navigated) return;
    Promise.resolve().then(() => {
      const /** @type {?} */ hasActiveLinks = this.hasActiveLinks();
      if (this.active !== hasActiveLinks) {
        this.active = hasActiveLinks;
        this.classes.forEach((c) => {
          if (hasActiveLinks) {
            this.renderer.addClass(this.element.nativeElement, c);
          } else {
            this.renderer.removeClass(this.element.nativeElement, c);
          }
        });
      }
    });
  }
/**
 * @param {?} router
 * @return {?}
 */
private isLinkActive(router: Router): (link: (RouterLink|RouterLinkWithHref)) => boolean {
    return (link: RouterLink | RouterLinkWithHref) =>
               router.isActive(link.urlTree, this.routerLinkActiveOptions.exact);
  }
/**
 * @return {?}
 */
private hasActiveLinks(): boolean {
    return this.links.some(this.isLinkActive(this.router)) ||
        this.linksWithHrefs.some(this.isLinkActive(this.router));
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[routerLinkActive]',
  exportAs: 'routerLinkActive',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Router, },
{type: ElementRef, },
{type: Renderer2, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'links': [{ type: ContentChildren, args: [RouterLink, {descendants: true}, ] },],
'linksWithHrefs': [{ type: ContentChildren, args: [RouterLinkWithHref, {descendants: true}, ] },],
'routerLinkActiveOptions': [{ type: Input },],
'routerLinkActive': [{ type: Input },],
};
}

function RouterLinkActive_tsickle_Closure_declarations() {
/** @type {?} */
RouterLinkActive.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RouterLinkActive.ctorParameters;
/** @type {?} */
RouterLinkActive.propDecorators;
/** @type {?} */
RouterLinkActive.prototype.links;
/** @type {?} */
RouterLinkActive.prototype.linksWithHrefs;
/** @type {?} */
RouterLinkActive.prototype.classes;
/** @type {?} */
RouterLinkActive.prototype.subscription;
/** @type {?} */
RouterLinkActive.prototype.active;
/** @type {?} */
RouterLinkActive.prototype.routerLinkActiveOptions;
/** @type {?} */
RouterLinkActive.prototype.router;
/** @type {?} */
RouterLinkActive.prototype.element;
/** @type {?} */
RouterLinkActive.prototype.renderer;
/** @type {?} */
RouterLinkActive.prototype.cdr;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
